/**
 * Error Codes Constants - Mirrors Backend
 *
 * Mirrors: provider-portal.Application/Common/Constants/ErrorCodes.cs
 *
 * Error codes follow the CAT_0xx convention:
 * - AUTH (Authentication/Authorization)
 * - VAL (Validation)
 * - BUS (Business Logic)
 * - SRV (Server/Infrastructure)
 * - NET (Network - frontend only, defined in clientErrorCodes.ts)
 *
 * Codes the backend also defines MUST match ErrorCodes.cs exactly — errorCodes.test.ts
 * reads that file and fails the build on any drift. Codes the client raises on its own
 * continue the same sequence; when the backend later claims one of those numbers, that
 * test fails and the client constant moves up.
 *
 * Usage: Use constants instead of string literals
 * @example
 * // ❌ Don't use magic strings
 * if (error.code === 'AUTH_001') { ... }
 *
 * // ✅ Use type-safe constants
 * import { ErrorCodes } from '@/constants/errorCodes';
 * if (error.code === ErrorCodes.AUTH_UNAUTHENTICATED) { ... }
 */

export const ErrorCodes = {
  // ============================================================================
  // Authentication/Authorization Errors (AUTH_0xx)
  // ============================================================================

  /** 401 - User not authenticated (not logged in, requires logout + redirect) */
  AUTH_UNAUTHENTICATED: 'AUTH_001',

  /** 403 - User authenticated but not authorized (forbidden resource, no logout) */
  AUTH_UNAUTHORIZED: 'AUTH_002',

  /** Invalid username/password combination */
  AUTH_INVALID_CREDENTIALS: 'AUTH_003',

  /** Authentication token has expired */
  AUTH_TOKEN_EXPIRED: 'AUTH_004',

  /** Session is invalid or has been terminated */
  AUTH_SESSION_INVALID: 'AUTH_005',

  /** No credentials provided in request */
  AUTH_MISSING_CREDENTIALS: 'AUTH_006',

  /** Provided key has invalid format */
  AUTH_INVALID_KEY: 'AUTH_007',

  /** Required key was not provided */
  AUTH_MISSING_KEY: 'AUTH_008',

  /** Failed validation attempts locked the invitation; no answer is checked */
  AUTH_INVITATION_LOCKED: 'AUTH_009',

  /** Another validation attempt for the same invitation is still being checked */
  AUTH_VALIDATION_IN_PROGRESS: 'AUTH_010',

  /** The bot check (Cloudflare Turnstile) was missing or failed; nothing else was checked */
  AUTH_CHALLENGE_FAILED: 'AUTH_011',

  // ============================================================================
  // Validation Errors (VAL_0xx)
  // ============================================================================

  /** Required field is missing */
  VAL_REQUIRED_FIELD: 'VAL_001',

  /** Field value has invalid format */
  VAL_INVALID_FORMAT: 'VAL_002',

  /** Field value is out of acceptable range */
  VAL_OUT_OF_RANGE: 'VAL_003',

  /** Duplicate value where uniqueness is required (client-only) */
  VAL_DUPLICATE_VALUE: 'VAL_004',

  // ============================================================================
  // Business Logic Errors (BUS_0xx)
  // ============================================================================

  /** Application form is locked and cannot be modified */
  BUS_APPLICATION_LOCKED: 'BUS_001',

  /** Section is incomplete or has missing required fields */
  BUS_SECTION_INCOMPLETE: 'BUS_002',

  /** Application/resource has already been submitted */
  BUS_ALREADY_SUBMITTED: 'BUS_003',

  /** Requested resource was not found */
  BUS_NOT_FOUND: 'BUS_004',

  /** Application form has not been signed yet */
  BUS_SIGNATURE_REQUIRED: 'BUS_005',

  /** Operation failed due to business rule violation (client-only) */
  BUS_OPERATION_FAILED: 'BUS_006',

  /** Invalid operation for current state (client-only) */
  BUS_INVALID_OPERATION: 'BUS_007',

  /** Operation conflicts with current state or existing data (client-only) */
  BUS_CONFLICT: 'BUS_008',

  // ============================================================================
  // Server/Infrastructure Errors (SRV_0xx)
  // ============================================================================

  /** Internal server error occurred */
  SRV_INTERNAL_ERROR: 'SRV_001',

  /** Database operation failed */
  SRV_DATABASE_ERROR: 'SRV_002',

  /** External service call failed */
  SRV_EXTERNAL_SERVICE_ERROR: 'SRV_003',

  /** Service temporarily unavailable (client-only) */
  SRV_SERVICE_UNAVAILABLE: 'SRV_004',

} as const;

/**
 * Type representing all possible error code values
 * Provides autocomplete and type checking for error codes
 */
export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

/**
 * Type guard to check if a string is a valid ErrorCode
 */
export function isErrorCode(code: string): code is ErrorCode {
  return Object.values(ErrorCodes).includes(code as ErrorCode);
}

/**
 * Get error category from error code
 * @param code - Error code (e.g., 'AUTH_001')
 * @returns Category ('auth', 'validation', 'business', 'server')
 */
export function getErrorCategory(code: ErrorCode): 'auth' | 'validation' | 'business' | 'server' {
  const prefix = code.split('_')[0];

  switch (prefix) {
    case 'AUTH':
      return 'auth';
    case 'VAL':
      return 'validation';
    case 'BUS':
      return 'business';
    case 'SRV':
      return 'server';
    default:
      return 'server';
  }
}
